// The Live sample's refused-write toast (PRD 117, greenbackapp/docs#151, D4).
//
// The demo api answers every write with 403 `demo_read_only`. Buttons stay
// live on purpose, so a visitor who tries something gets a kind sentence
// rather than a dead control. apiFetch reports each refusal here, and the
// listener mounted once at the app root (DemoReadOnlyListener) shows the
// toast. Same shape as businessReadOnlyNotifier: throttled so a screen that
// fires three writes on one click produces one toast.
//
// SILENT PATHS are writes the visitor did not ask for: the app records that a
// thread was read, or that the setup guide was put away. Telling someone
// "changes aren't saved" when they only opened a message is noise.

type Listener = () => void;

const THROTTLE_MS = 5000;

export const DEMO_READ_ONLY_CODE = "demo_read_only";

/**
 * Background writes that must not toast, as method + path patterns matched
 * against the request path without its query string. There is no
 * tour-progress write in the web client today; add it here if one appears.
 */
export const DEMO_SILENT_PATHS: ReadonlyArray<{ method: string; pattern: RegExp }> = [
  // messagingService.markRead: opening a thread marks it read.
  { method: "POST", pattern: /^\/?api\/v1\/business\/messages\/[^/]+\/read$/ },
  // businessSetupGuideService.dismiss: the setup guide being put away.
  { method: "POST", pattern: /^\/?api\/v1\/business\/setup_guide\/dismiss$/ },
];

export function isDemoSilentPath(method: string, path: string): boolean {
  const bare = path.split("?")[0];
  const upper = method.toUpperCase();
  return DEMO_SILENT_PATHS.some(entry => entry.method === upper && entry.pattern.test(bare));
}

let listeners = new Set<Listener>();
let lastNotifiedAt = 0;

/** Subscribe to demo read-only refusals; returns the unsubscribe function. */
export function subscribeDemoReadOnly(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Called by apiFetch on a 403 demo_read_only response to a write the visitor made. */
export function notifyDemoReadOnly(): void {
  const now = Date.now();
  if (now - lastNotifiedAt < THROTTLE_MS) return;
  lastNotifiedAt = now;
  listeners.forEach(listener => listener());
}

/** Test hook: clears listeners and the throttle window. */
export function resetDemoReadOnlyNotifier(): void {
  listeners = new Set();
  lastNotifiedAt = 0;
}
